import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://arvantasolutions.in';
const ORG_ID = `${SITE_URL}/#organization`;
const LOGO = 'https://customer-assets.emergentagent.com/job_demo-arvanta/artifacts/1ljd19ky_Arvanta_Solutions.png';

const PROVIDER = { '@id': ORG_ID };

const SERVICES = [
  {
    id: 'tender-bidding',
    name: 'Tender Bidding Solutions',
    description:
      'End-to-end tender bidding solutions for Indian businesses — opportunity discovery, bid writing, technical & financial proposal preparation, GeM registration, e-tender submission, EMD/BG support, and post-award compliance. Specialists in government, PSU and private sector tenders.',
    url: `${SITE_URL}/#tender-bidding`,
    serviceType: 'Tender Bidding Consulting',
    category: 'Government Tender Consulting',
    keywords: [
      'tender bidding solutions',
      'government tender consultants India',
      'GeM tender bidding',
      'e-tender bidding Vadodara',
      'PSU tender support',
      'bid writing services India',
      'tender compliance consultants',
      'EMD and bank guarantee support',
    ],
    offers: [
      'Opportunity Identification',
      'Technical Bid Preparation',
      'Financial Bid Strategy',
      'GeM Portal Registration & Bidding',
      'E-Tender Submission',
      'Post-Award Compliance',
    ],
  },
  {
    id: 'digital-growth',
    name: 'Digital Growth Services',
    description:
      'Full-funnel digital growth services for Indian corporates — technical SEO, content marketing, Google / Meta / LinkedIn paid ads, social media management, CRO and analytics dashboards. Measurable B2B pipeline growth from search and paid channels.',
    url: `${SITE_URL}/#digital-growth`,
    serviceType: 'Digital Marketing',
    category: 'Digital Marketing Services',
    keywords: [
      'digital growth services India',
      'B2B digital marketing agency Vadodara',
      'performance marketing India',
      'LinkedIn ads for B2B',
      'Google Ads management India',
      'content marketing agency Gujarat',
      'conversion rate optimisation',
    ],
    offers: [
      'Technical & On-page SEO',
      'Content Marketing',
      'Google & Meta Ads',
      'LinkedIn B2B Campaigns',
      'Social Media Management',
      'Analytics & CRO',
    ],
  },
  {
    id: 'website-development',
    name: 'Website Development',
    description:
      'Custom website development for Indian enterprises — fast, responsive, SEO-ready corporate websites, product sites, portals and lead-generation funnels built on modern React / Next.js stacks with Core Web Vitals-first performance.',
    url: `${SITE_URL}/#website-development`,
    serviceType: 'Website Development',
    category: 'Web Development Services',
    keywords: [
      'website development company India',
      'corporate website design Vadodara',
      'React Next.js development India',
      'SEO ready website development',
      'B2B website development',
      'landing page design and development',
      'business website Gujarat',
    ],
    offers: [
      'Corporate & Brand Websites',
      'Lead-Gen Landing Pages',
      'E-commerce Storefronts',
      'Portals & Dashboards',
      'Website Redesign & Migration',
      'Core Web Vitals Optimisation',
    ],
  },
  {
    id: 'custom-software',
    name: 'Customized Business Softwares',
    description:
      'Customized business software development for Indian enterprises — bespoke ERP, CRM, workflow automation, inventory, procurement and reporting platforms. Tailor-made integrations with Tally, SAP, Zoho and GeM.',
    url: `${SITE_URL}/#custom-software`,
    serviceType: 'Custom Software Development',
    category: 'Enterprise Software Development',
    keywords: [
      'customized business software India',
      'custom ERP development India',
      'bespoke CRM software Vadodara',
      'workflow automation software',
      'procurement software development',
      'Tally and SAP integration',
      'enterprise software company Gujarat',
    ],
    offers: [
      'Custom ERP Development',
      'CRM & Sales Automation',
      'Workflow & Process Automation',
      'Inventory & Procurement Systems',
      'Reporting & BI Dashboards',
      'Third-party API Integrations',
    ],
  },
];

const buildServiceSchema = (svc) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${SITE_URL}/#service-${svc.id}`,
  name: svc.name,
  alternateName: svc.keywords.slice(0, 3),
  description: svc.description,
  serviceType: svc.serviceType,
  category: svc.category,
  url: svc.url,
  provider: PROVIDER,
  brand: PROVIDER,
  image: LOGO,
  areaServed: [
    { '@type': 'Country', name: 'India' },
    { '@type': 'AdministrativeArea', name: 'Gujarat' },
    { '@type': 'City', name: 'Vadodara' },
  ],
  audience: {
    '@type': 'BusinessAudience',
    audienceType: 'Enterprises, SMEs, Procurement Teams, CFOs, CEOs',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: `${svc.name} — Offerings`,
    itemListElement: svc.offers.map((o, idx) => ({
      '@type': 'Offer',
      itemOffered: { '@type': 'Service', name: o },
      position: idx + 1,
    })),
  },
  keywords: svc.keywords.join(', '),
});

const SEO = () => {
  const servicesKeywords = SERVICES.flatMap((s) => s.keywords).join(', ');

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Services', item: `${SITE_URL}/#services` },
      ...SERVICES.map((s, i) => ({
        '@type': 'ListItem',
        position: 3 + i,
        name: s.name,
        item: s.url,
      })),
    ],
  };

  const itemList = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Arvanta Solutions — Services',
    itemListElement: SERVICES.map((s, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      url: s.url,
      name: s.name,
    })),
  };

  // Professional service with aggregate rating (helps rich snippets on Google)
  const professionalService = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': `${SITE_URL}/#professional-service`,
    name: 'Arvanta Solutions',
    url: SITE_URL,
    image: LOGO,
    logo: LOGO,
    description:
      'Arvanta Solutions is a leading tender bidding and digital growth consultancy in Vadodara, India. We help enterprises win government, PSU and private tenders while scaling B2B digital pipelines through SEO, paid media, website development and custom business software.',
    telephone: '+91-72848-25958',
    email: 'info@arvantasolutions.in',
    priceRange: '₹₹',
    foundingDate: '2020',
    numberOfEmployees: { '@type': 'QuantitativeValue', minValue: 10, maxValue: 50 },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '305, Opal Square, Behind Express Hotel, Alkapuri',
      addressLocality: 'Vadodara',
      addressRegion: 'Gujarat',
      postalCode: '390007',
      addressCountry: 'IN',
    },
    geo: { '@type': 'GeoCoordinates', latitude: '22.3072', longitude: '73.1812' },
    areaServed: [
      { '@type': 'Country', name: 'India' },
      { '@type': 'AdministrativeArea', name: 'Gujarat' },
      { '@type': 'AdministrativeArea', name: 'Maharashtra' },
      { '@type': 'AdministrativeArea', name: 'Rajasthan' },
      { '@type': 'AdministrativeArea', name: 'Madhya Pradesh' },
    ],
    knowsAbout: [
      'Government Tender Bidding',
      'GeM Portal Bidding',
      'PSU Tender Consulting',
      'E-Tender Submission',
      'Bid Writing and Documentation',
      'EMD and Bank Guarantee Support',
      'B2B Digital Marketing',
      'Technical SEO',
      'Google Ads & LinkedIn Ads',
      'React / Next.js Website Development',
      'Custom ERP and CRM Development',
      'Workflow Automation Software',
    ],
    makesOffer: SERVICES.map((s) => ({
      '@type': 'Offer',
      itemOffered: { '@type': 'Service', name: s.name, url: s.url },
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '37',
      bestRating: '5',
      worstRating: '1',
    },
    review: [
      {
        '@type': 'Review',
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
        author: { '@type': 'Organization', name: 'Flameproof LED Pvt. Ltd.' },
        reviewBody:
          'Arvanta managed our ₹4Cr railway tender end-to-end. Zero compliance misses, won on first submission.',
      },
      {
        '@type': 'Review',
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
        author: { '@type': 'Organization', name: 'Stud Bolts & Fasteners Pvt. Ltd.' },
        reviewBody:
          '8 PSU tenders won in a 12-month engagement. Their bid-strategy process is airtight.',
      },
    ],
    sameAs: [
      'https://www.linkedin.com/company/arvantasolutions',
      'https://www.google.com/maps?cid=arvantasolutions',
    ],
  };

  // Site navigation structured data (helps Google show sitelinks)
  const siteNav = {
    '@context': 'https://schema.org',
    '@type': 'SiteNavigationElement',
    name: ['Home', 'Why Us', 'Services', 'Methodology', 'Case Studies', 'FAQ', 'Contact'],
    url: [
      `${SITE_URL}/`,
      `${SITE_URL}/#why-corporates`,
      `${SITE_URL}/#services`,
      `${SITE_URL}/#why`,
      `${SITE_URL}/#testimonials`,
      `${SITE_URL}/#faq`,
      `${SITE_URL}/#contact`,
    ],
  };

  return (
    <Helmet>
      {/* Primary page meta — overrides the baseline in index.html for freshness */}
      <title>Arvanta Solutions | Tender Bidding, Digital Growth, Web & Custom Software — Vadodara, India</title>
      <meta
        name="description"
        content="Arvanta Solutions helps Indian enterprises win government, PSU and private tenders and scale digital revenue. Expert tender bidding, SEO, Google Ads, website development and custom ERP/CRM software. 250+ tenders won, ₹165Cr+ bid value, 94% success rate."
      />
      <meta name="subject" content="Tender Bidding Solutions, Digital Growth, Website Development, Custom Business Software" />
      <meta name="rating" content="General" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="coverage" content="Worldwide" />
      <meta name="target" content="all" />
      <meta name="HandheldFriendly" content="true" />
      <meta name="format-detection" content="telephone=yes" />
      <meta httpEquiv="content-language" content="en-IN" />
      <link rel="canonical" href={`${SITE_URL}/`} />
      <link rel="alternate" hrefLang="en-IN" href={`${SITE_URL}/`} />
      <link rel="alternate" hrefLang="en" href={`${SITE_URL}/`} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/`} />

      {/* Per-service meta tags (keyword-rich) */}
      <meta
        name="keywords"
        content={`tender bidding India, government tender consultants, GeM tender consultants, digital marketing Vadodara, website development India, custom business software, ${servicesKeywords}, Arvanta Solutions`}
      />
      <meta name="service:tender-bidding" content={SERVICES[0].description} />
      <meta name="service:digital-growth" content={SERVICES[1].description} />
      <meta name="service:website-development" content={SERVICES[2].description} />
      <meta name="service:custom-software" content={SERVICES[3].description} />

      {/* Additional discoverability tags */}
      <meta
        property="article:tag"
        content="Tender Bidding, Digital Growth, Website Development, Custom Business Software"
      />
      <meta property="business:contact_data:street_address" content="305, Opal Square, Behind Express Hotel, Alkapuri" />
      <meta property="business:contact_data:locality" content="Vadodara" />
      <meta property="business:contact_data:region" content="Gujarat" />
      <meta property="business:contact_data:postal_code" content="390007" />
      <meta property="business:contact_data:country_name" content="India" />
      <meta property="business:contact_data:email" content="info@arvantasolutions.in" />
      <meta property="business:contact_data:phone_number" content="+91-72848-25958" />

      {/* Preconnect / DNS prefetch for faster LCP (SEO bonus for Core Web Vitals) */}
      <link rel="preconnect" href="https://images.pexels.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://videos.pexels.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://images.unsplash.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="https://customer-assets.emergentagent.com" />

      {/* Per-service JSON-LD Service schema */}
      {SERVICES.map((svc) => (
        <script key={svc.id} type="application/ld+json">
          {JSON.stringify(buildServiceSchema(svc))}
        </script>
      ))}

      {/* Breadcrumb schema linking all four services */}
      <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>

      {/* ItemList schema aggregating services */}
      <script type="application/ld+json">{JSON.stringify(itemList)}</script>

      {/* ProfessionalService with aggregate rating and reviews */}
      <script type="application/ld+json">{JSON.stringify(professionalService)}</script>

      {/* Site navigation element for Google sitelinks */}
      <script type="application/ld+json">{JSON.stringify(siteNav)}</script>
    </Helmet>
  );
};

export default SEO;
export { SERVICES as SERVICE_SEO_DATA };
